import React from 'react'

export interface IDragAndDropTargetOptions {
  /**
   * Callback invoked when a file is dropped, with the dropped File as its only parameter.
   */
  onFileDrop: (file: File) => void

  /**
   * If set, drag and drop is disabled.
   */
  disabled?: boolean
}

export interface IDragAndDropTargetResult {
  /**
   * A boolean indicating if there is a file drop incoming (meaning the dragged file entered the app).
   */
  dropMode: boolean

  /**
   * A boolean indicating if the file drop is over the target area, and is ready for the final drop.
   */
  dropReady: boolean

  /**
   * Props to be passed to the target element acting as the drop area.
   */
  targetProps: {
    onDragOver: (e: React.DragEvent) => void
    onDragLeave: (e: React.DragEvent) => void
    onDrop: (e: React.DragEvent) => void
  }
}

/**
 * React hook to set up handling and UX of drag and drop for a particular DOM target (such as a div).
 */
export function useDragAndDropTarget(opts: IDragAndDropTargetOptions): IDragAndDropTargetResult {
  const { onFileDrop, disabled } = opts
  const [isDropMode, setDropMode] = React.useState(false)
  const [isDropReady, setDropReady] = React.useState(false)
  const timeoutHandleRef = React.useRef<number | null>(null)

  const handleDragOver = React.useCallback((e: React.DragEvent) => {
    e.preventDefault()

    if (disabled) return

    if (!isDropReady) {
      setDropReady(true)
    }
  }, [isDropReady, disabled])

  const handleDrop = React.useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (disabled) return

    setDropMode(false)
    setDropReady(false)

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onFileDrop(e.dataTransfer.files[0])
    }
  }, [onFileDrop, disabled])

  const handleDragLeave = React.useCallback(() => {
    if (disabled) return

    if (isDropReady) {
      setDropReady(false)
    }
  }, [isDropReady, disabled])

  const handleWindowDragOver = React.useCallback(() => {
    if (timeoutHandleRef.current) {
      window.clearTimeout(timeoutHandleRef.current)
    }

    if (!isDropMode) {
      setDropMode(true)
    }
  }, [isDropMode])

  const handleWindowDragLeave = React.useCallback(() => {
    if (timeoutHandleRef.current) {
      window.clearTimeout(timeoutHandleRef.current)
    }

    if (isDropMode) {
      timeoutHandleRef.current = window.setTimeout(() => {
        setDropMode(false)
      }, 200)
    }
  }, [isDropMode])

  React.useEffect(() => {
    window.addEventListener('dragover', handleWindowDragOver)
    window.addEventListener('dragleave', handleWindowDragLeave)

    return () => {
      window.removeEventListener('dragover', handleWindowDragOver)
      window.removeEventListener('dragleave', handleWindowDragLeave)
    }
  }, [handleWindowDragOver, handleWindowDragLeave])

  return {
    dropMode: isDropMode,
    dropReady: isDropReady,
    targetProps: {
      onDragLeave: handleDragLeave,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
    },
  }
}
